import { ChevronDown } from 'lucide-react'
import React, { useState } from 'react'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from 'src/component/ui/drawer'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/component/ui/select'
import { Gender } from 'src/dao/core-dao'
import { useMediaQuery } from 'src/hook/use-media-query'
import { cn } from 'src/lib/utils'

interface GenderFieldProps {
  value?: Gender | string
  onChange: (value: string) => void
  error?: string
  disabled?: boolean
}

const getGenderDisplayText = (gender?: Gender | string): string => {
  switch (gender) {
    case Gender.MALE:
      return 'Male'
    case Gender.FEMALE:
      return 'Female'
    case Gender.NON_BINARY:
      return 'Non-binary'
    case Gender.N_A:
      return 'Prefer not to say'
    default:
      return 'Select gender'
  }
}

export const GenderField = ({ value, onChange, error, disabled }: GenderFieldProps): React.ReactElement => {
  const isMobile = useMediaQuery('(max-width: 556px)')
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const handleGenderSelect = (selectedValue: string): void => {
    onChange(selectedValue)
    setIsDrawerOpen(false)
  }

  const genderOptions = [
    { value: Gender.MALE, label: 'Male' },
    { value: Gender.FEMALE, label: 'Female' },
    { value: Gender.NON_BINARY, label: 'Non-binary' },
    { value: Gender.N_A, label: 'Prefer not to say' },
  ]

  if (isMobile) {
    return (
      <div className="space-y-2">
        <label htmlFor="gender" className="block text-sm font-roboto-condensed font-bold text-mist">
          Gender
        </label>
        <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
          <DrawerTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              className={cn(
                'flex h-9 w-full items-center justify-between rounded-md bg-secondary-gray px-3 py-2 text-sm',
                'font-evogria font-normal text-light-gray placeholder:text-mushroom uppercase',
                'focus-visible:outline-none',
                'disabled:cursor-not-allowed disabled:opacity-50',
                error && 'border border-danger',
              )}
            >
              <span className={cn('line-clamp-1 text-sm font-evogria text-light-gray uppercase', !value && 'text-mushroom')}>
                {getGenderDisplayText(value)}
              </span>
              <ChevronDown className="h-4 w-4 text-light-gray" />
            </button>
          </DrawerTrigger>
          <DrawerContent className="bg-secondary-gray">
            <DrawerHeader>
              <DrawerTitle className="font-roboto-condensed font-bold text-mist text-center">Select Gender</DrawerTitle>
            </DrawerHeader>
            <div className="px-4 pb-4 space-y-2">
              {genderOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleGenderSelect(option.value)}
                  className={cn(
                    'w-full text-left rounded-md bg-secondary-gray px-4 py-3 text-sm',
                    'font-evogria font-normal text-light-gray uppercase',
                    'hover:bg-primary-gray transition-colors',
                    value === option.value && 'bg-primary-gray',
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </DrawerContent>
        </Drawer>
        {error && <p className="text-sm font-roboto-condensed text-danger">{error}</p>}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <label htmlFor="gender" className="block text-sm font-roboto-condensed font-bold text-mist">
        Gender
      </label>
      <Select value={value || ''} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger>
          <SelectValue placeholder={<p className="text-sm font-evogria text-mushroom uppercase">Select gender</p>} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={Gender.MALE}>
            <p className="text-sm font-evogria text-light-gray uppercase">Male</p>
          </SelectItem>
          <SelectItem value={Gender.FEMALE}>
            <p className="text-sm font-evogria text-light-gray uppercase">Female</p>
          </SelectItem>
          <SelectItem value={Gender.NON_BINARY}>
            <p className="text-sm font-evogria text-light-gray uppercase">Non-binary</p>
          </SelectItem>
          <SelectItem value={Gender.N_A}>
            <p className="text-sm font-evogria text-light-gray uppercase">Prefer not to say</p>
          </SelectItem>
        </SelectContent>
      </Select>
      {error && <p className="text-sm font-roboto-condensed text-danger">{error}</p>}
    </div>
  )
}
